/**
 * PastLivesCardView — single past-life card as rendered in the carousel.
 *
 * Layout: portrait (4:5 aspect) → era + region kicker → profession title →
 * narrative paragraph → token chips. Share button is per-card.
 *
 * Hero cards get a small "HERO" badge top-right; non-hero get a "LAYER N"
 * pill from layerDisplayRu (already translated by the backend).
 */
import { useTranslation } from 'react-i18next';
import type { PastLivesCard } from '../../hooks/usePastLivesRun';
import styles from './PastLivesCardView.module.css';

export interface PastLivesCardViewProps {
  card: PastLivesCard;
  position: number;
  total: number;
  onShare?: (card: PastLivesCard) => void;
  testId?: string;
}

export function PastLivesCardView({
  card,
  position,
  total,
  onShare,
  testId = 'past-lives-card',
}: PastLivesCardViewProps): JSX.Element {
  const { t } = useTranslation('flows');
  const eraLabel = card.eraYearEstimate
    ? `${card.eraYearEstimate > 0 ? '~' : ''}${card.eraYearEstimate}`
    : card.era;

  return (
    <article className={styles.root} data-testid={testId} data-card-id={card.id}>
      <div className={styles.portraitWrap}>
        {card.portraitUrl ? (
          <img
            src={card.portraitUrl}
            alt={card.profession ?? card.era}
            className={styles.portrait}
            loading="lazy"
          />
        ) : (
          <div className={styles.portraitFallback} aria-hidden="true">
            <span className={styles.portraitGlyph}>✦</span>
          </div>
        )}

        {card.isHero ? (
          <span className={styles.badgeHero}>
            {t('pastLives.card.heroBadge', { defaultValue: 'HERO' })}
          </span>
        ) : (
          <span className={styles.badgeLayer}>{card.layerDisplayRu}</span>
        )}

        <span className={styles.positionPill}>
          {t('pastLives.card.position', {
            position,
            total,
            defaultValue: '{{position}} / {{total}}',
          })}
        </span>
      </div>

      <div className={styles.body}>
        <div className={styles.kicker}>
          <span>{eraLabel}</span>
          <span aria-hidden="true">·</span>
          <span>{card.region}</span>
        </div>

        <h3 className={styles.title}>
          {card.profession ??
            t('pastLives.card.unknownProfession', { defaultValue: 'A life remembered' })}
        </h3>

        <p className={styles.narrative}>{card.narrative}</p>

        {card.tokens.length > 0 ? (
          <ul className={styles.tokens} aria-label={t('pastLives.card.tokensLabel', { defaultValue: 'Karmic threads' })}>
            {card.tokens.slice(0, 6).map((tok) => (
              <li key={tok} className={styles.token}>
                {tok}
              </li>
            ))}
          </ul>
        ) : null}

        {onShare ? (
          <button
            type="button"
            className={styles.shareBtn}
            onClick={() => onShare(card)}
            data-testid="past-lives-share-card"
          >
            {t('pastLives.card.share', { defaultValue: 'Share this life' })}
          </button>
        ) : null}
      </div>
    </article>
  );
}

export default PastLivesCardView;
